<script setup>
import { computed, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { escapeHtml } from 'shared/helpers/HTMLSanitizer';

const props = defineProps({
  group: { type: Object, default: null },
});

// A group description is plain text that often carries links. It is rendered as
// written, only with the addresses made clickable — trailing punctuation stays
// out of the link so "veja https://takeat.app." keeps its full stop.
const URL_PATTERN = /https?:\/\/[^\s<]+/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

const isDescriptionOpen = ref(false);
const areMembersOpen = ref(false);

const description = computed(() => props.group?.description?.trim() || '');

const linkedDescription = computed(() =>
  escapeHtml(description.value).replace(URL_PATTERN, match => {
    const trailing = TRAILING_PUNCTUATION.exec(match)?.[0] ?? '';
    const url = match.slice(0, match.length - trailing.length);

    return `<a href="${url}" rel="noreferrer noopener nofollow" class="text-n-whatsapp-accent hover:underline">${url}</a>${trailing}`;
  })
);

// Admins first, so the people to talk to are at the top of a long list.
const members = computed(() => {
  const participants = props.group?.participants ?? [];

  return [...participants].sort(
    (a, b) => Number(Boolean(b.admin)) - Number(Boolean(a.admin))
  );
});

const hasContent = computed(() => description.value || members.value.length);
</script>

<template>
  <div v-show="hasContent" class="flex flex-col w-full gap-2">
    <div v-if="description" class="w-full">
      <button
        class="flex items-center justify-between w-full gap-2 text-sm font-medium text-n-slate-12"
        @click="isDescriptionOpen = !isDescriptionOpen"
      >
        <span class="flex items-center gap-1.5 min-w-0">
          <span class="text-base i-lucide-info text-n-slate-10" />
          <span class="truncate">
            {{ $t('CONTACT_PANEL.WHATSAPP_GROUP.DESCRIPTION') }}
          </span>
        </span>
        <span
          class="text-base text-n-slate-10"
          :class="
            isDescriptionOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
          "
        />
      </button>
      <p
        v-dompurify-html="linkedDescription"
        class="mt-1 mb-0 text-sm break-words whitespace-pre-line text-n-slate-11"
        :class="{ 'line-clamp-2': !isDescriptionOpen }"
      />
    </div>

    <div v-if="members.length" class="w-full">
      <button
        class="flex items-center justify-between w-full gap-2 text-sm font-medium text-n-slate-12"
        @click="areMembersOpen = !areMembersOpen"
      >
        <span class="flex items-center gap-1.5 min-w-0">
          <span class="text-base i-lucide-users text-n-slate-10" />
          <span class="truncate">
            {{
              $t('CONTACT_PANEL.WHATSAPP_GROUP.MEMBERS', {
                count: members.length,
              })
            }}
          </span>
        </span>
        <span
          class="text-base text-n-slate-10"
          :class="
            areMembersOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
          "
        />
      </button>
      <ul v-if="areMembersOpen" class="flex flex-col gap-2 mt-2 mb-0">
        <li
          v-for="(member, index) in members"
          :key="member.phone || index"
          class="flex items-center gap-2 min-w-0"
        >
          <Avatar
            :name="member.name || member.phone || ''"
            :src="member.avatar_url"
            :size="28"
            rounded-full
          />
          <div class="flex flex-col min-w-0">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="text-sm truncate text-n-slate-12">
                {{ member.name || member.phone }}
              </span>
              <span
                v-if="member.admin"
                class="flex-shrink-0 px-1.5 py-px text-xs rounded-full text-n-slate-11 bg-n-alpha-2"
              >
                {{ $t('CONTACT_PANEL.WHATSAPP_GROUP.ADMIN') }}
              </span>
            </div>
            <span
              v-if="member.name && member.phone"
              class="text-xs text-n-slate-10"
            >
              {{ member.phone }}
            </span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
